/**
 * Chess pieces as inline SVG.
 *
 * Drawn as paths rather than loaded as images so the board renders instantly,
 * works offline, needs no asset CDN, and can be recoloured by CSS variables to
 * match the board theme. The outlines are the classic Cburnett shapes, which
 * are the ones nearly every chess site uses, simplified into single paths.
 */

export type PieceColor = 'w' | 'b';
export type PieceType = 'p' | 'n' | 'b' | 'r' | 'q' | 'k';

/** A piece as chess.js reports it. */
export interface Piece {
  type: PieceType;
  color: PieceColor;
}

const PATHS: Record<PieceType, string> = {
  // Pawn: head, neck, base.
  p: 'M22.5 9c-2.21 0-4 1.79-4 4 0 .89.29 1.71.78 2.38C17.33 16.5 16 18.59 16 21c0 2.03.94 3.84 2.41 5.03-3 1.06-7.41 5.55-7.41 13.47h23c0-7.92-4.41-12.41-7.41-13.47 1.47-1.19 2.41-3 2.41-5.03 0-2.41-1.33-4.5-3.28-5.62.49-.67.78-1.49.78-2.38 0-2.21-1.79-4-4-4z',
  // Knight: the classic horse head.
  n: 'M22 10c10.5 1 16.5 8 16 29H15c0-9 10-6.5 8-21M24 18c.38 2.91-5.55 7.37-8 9-3 2-2.82 4.34-5 4-1.042-.94 1.41-3.04 0-3-1 0 .19 1.23-1 2-1 0-4.003 1-4-4 0-2 6-12 6-12s1.89-1.9 2-3.5c-.73-.994-.5-2-.5-3 1-1 3 2.5 3 2.5h2s.78-1.992 2.5-3c1 0 1 3 1 3',
  // Bishop: mitre, body, base.
  b: 'M9 36c3.39-.97 10.11.43 13.5-2 3.39 2.43 10.11 1.03 13.5 2 0 0 1.65.54 3 2-.68.97-1.65.99-3 .5-3.39-.97-10.11.46-13.5-1-3.39 1.46-10.11.03-13.5 1-1.354.49-2.323.47-3-.5 1.354-1.94 3-2 3-2zM15 32c2.5 2.5 12.5 2.5 15 0 .5-1.5 0-2 0-2 0-2.5-2.5-4-2.5-4 5.5-1.5 6-11.5-5-15.5-11 4-10.5 14-5 15.5 0 0-2.5 1.5-2.5 4 0 0-.5.5 0 2zM25 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 1 1 5 0z',
  // Rook: crenellations, body, base.
  r: 'M9 39h27v-3H9v3zM12 36v-4h21v4H12zM11 14V9h4v2h5V9h5v2h5V9h4v5M34 14l-3 3H14l-3-3M31 17v12.5H14V17M31 29.5l1.5 2.5h-20l1.5-2.5M11 14h23',
  // Queen: crown with orbs.
  q: 'M9 26c8.5-1.5 21-1.5 27 0l2.5-12.5L31 25l-.3-14.1-5.2 13.6-3-14.5-3 14.5-5.2-13.6L14 25 6.5 13.5 9 26zM9 26c0 2 1.5 2 2.5 4 1 1.5 1 1 .5 3.5-1.5 1-1.5 2.5-1.5 2.5-1.5 1.5.5 2.5.5 2.5 6.5 1 16.5 1 23 0 0 0 1.5-1 0-2.5 0 0 .5-1.5-1-2.5-.5-2.5-.5-2 .5-3.5 1-2 2.5-2 2.5-4-8.5-1.5-18.5-1.5-27 0zM11.5 30c3.5-1 18.5-1 22 0M12 33.5c6-1 15-1 21 0',
  // King: cross, crown, body.
  k: 'M22.5 11.63V6M20 8h5M22.5 25s4.5-7.5 3-10.5c0 0-1-2.5-3-2.5s-3 2.5-3 2.5c-1.5 3 3 10.5 3 10.5M11.5 37c5.5 3.5 15.5 3.5 21 0v-7s9-4.5 6-10.5c-4-6.5-13.5-3.5-16 4V27v-3.5c-2.5-7.5-12-10.5-16-4-3 6 6 10.5 6 10.5v7z',
};

/**
 * Pieces whose shape reads as an outline rather than a filled silhouette —
 * they need a visible stroke in both colors or they lose all their detail.
 */
const STROKE_ONLY: PieceType[] = ['r', 'k'];

export function PieceSvg({ piece, size = '100%' }: { piece: Piece; size?: number | string }) {
  const isWhite = piece.color === 'w';
  const fill = isWhite ? '#f7f3ea' : '#2b2724';
  const stroke = isWhite ? '#2b2724' : '#0d0c0b';
  const strokeOnly = STROKE_ONLY.includes(piece.type);

  return (
    <svg
      viewBox="0 0 45 45"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', pointerEvents: 'none' }}
    >
      <g
        fill={strokeOnly ? fill : fill}
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        // A soft drop shadow lifts the piece off the square without needing
        // a separate highlight layer.
        style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))' }}
      >
        <path d={PATHS[piece.type]} />
      </g>
    </svg>
  );
}

const NAMES: Record<PieceType, string> = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king',
};

/** "white knight" — used for screen readers and move announcements. */
export function pieceName(piece: Piece): string {
  return `${piece.color === 'w' ? 'white' : 'black'} ${NAMES[piece.type]}`;
}
